import React from 'react';
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

export interface SuccessMessageProps {
  message: string;
  onDismiss?: () => void;
  title?: string;
  variant?: 'success' | 'info' | 'warning';
  className?: string;
  autoHide?: boolean;
  autoHideDelay?: number;
}

const SuccessMessage: React.FC<SuccessMessageProps> = ({
  message,
  onDismiss,
  title = 'Succès',
  variant = 'success',
  className = '',
  autoHide = false,
  autoHideDelay = 5000
}) => {
  // Auto-hide functionality
  React.useEffect(() => {
    if (autoHide && onDismiss) {
      const timer = setTimeout(() => {
        onDismiss();
      }, autoHideDelay);

      return () => clearTimeout(timer);
    }
  }, [autoHide, onDismiss, autoHideDelay]);

  // Styles selon la variante
  const getStyles = () => {
    switch (variant) {
      case 'info':
        return {
          container: 'bg-blue-50 border-blue-200',
          icon: 'text-blue-600',
          title: 'text-blue-800',
          text: 'text-blue-700',
          dismissButton: 'text-blue-400 hover:text-blue-600'
        };
      case 'warning':
        return {
          container: 'bg-yellow-50 border-yellow-200',
          icon: 'text-yellow-600',
          title: 'text-yellow-800',
          text: 'text-yellow-700',
          dismissButton: 'text-yellow-400 hover:text-yellow-600'
        };
      default: // success
        return {
          container: 'bg-green-50 border-green-200',
          icon: 'text-green-600',
          title: 'text-green-800',
          text: 'text-green-700',
          dismissButton: 'text-green-400 hover:text-green-600'
        };
    }
  };

  const styles = getStyles();

  // Ne pas afficher si le message est vide
  if (!message || message.trim() === '') {
    return null;
  }

  return (
    <div className={`rounded-lg border p-4 ${styles.container} ${className}`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <CheckCircleIcon className={`h-5 w-5 ${styles.icon}`} />
        </div>
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${styles.title}`}>
            {title}
          </h3>
          <div className={`mt-2 text-sm ${styles.text}`}>
            <p>{message}</p>
          </div>
        </div>
        {onDismiss && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                onClick={onDismiss}
                className={`inline-flex rounded-md p-1.5 ${styles.dismissButton} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-600`}
              >
                <span className="sr-only">Fermer</span>
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuccessMessage;
